import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

/**
 * useChecklistDetail — Fatia E / Frente A (render dos itens no ChecklistBlock).
 *
 * Carrega UM checklist COM seus itens via `GET /v1/checklists/:id`
 * (`api.getChecklist`), que o `useChecklists` (lista/resumo) não traz. `enabled`
 * só quando há id. O recálculo pós-ação chega tanto pela invalidação da mutation
 * quanto pelo WS `checklist.updated` (consumido pelo `useChecklists`).
 */
export function useChecklistDetail(
  checklistId: string | undefined,
): ReturnType<typeof useQuery<Awaited<ReturnType<typeof api.getChecklist>>, Error>> {
  return useQuery({
    queryKey: ['checklist-detail', checklistId],
    enabled: checklistId !== undefined,
    queryFn: () => {
      if (checklistId === undefined) throw new Error('checklistId ausente');
      return api.getChecklist(checklistId);
    },
  });
}

/** Invalida as duas superfícies do checklist (detalhe com itens + resumo da lista). */
function invalidateChecklistQueries(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['checklist-detail'] });
  void qc.invalidateQueries({ queryKey: ['checklists'] });
}

/** Toast honesto de falha (rede/409 item-já-mudou/403/400). NÃO fica em silêncio. */
function toastChecklistError(): void {
  useToastStore.getState().push({
    variant: 'error',
    title: 'Não foi possível atualizar o item',
    description: 'O item pode ter mudado de estado. Recarregue e tente novamente.',
  });
}

/**
 * useFulfillChecklistItem — marca um item como cumprido
 * (`POST /v1/checklist-items/:id/fulfill`). Registra proveniência humana
 * (o backend usa o token; `fulfilledViaAgent=false`). Invalida detalhe + resumo.
 */
export function useFulfillChecklistItem(): ReturnType<
  typeof useMutation<void, Error, { itemId: string; notes?: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, notes }: { itemId: string; notes?: string }) =>
      api.fulfillChecklistItem(itemId, notes !== undefined ? { notes } : {}),
    onSuccess: () => invalidateChecklistQueries(qc),
    onError: toastChecklistError,
  });
}

/**
 * useMarkChecklistItemNotApplicable — marca item como não-aplicável com motivo
 * (`POST /v1/checklist-items/:id/mark-not-applicable`; motivo ≥5 chars exigido
 * pelo backend). Invalida detalhe + resumo.
 */
export function useMarkChecklistItemNotApplicable(): ReturnType<
  typeof useMutation<void, Error, { itemId: string; reason: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, reason }: { itemId: string; reason: string }) =>
      api.markChecklistItemNotApplicable(itemId, reason),
    onSuccess: () => invalidateChecklistQueries(qc),
    onError: toastChecklistError,
  });
}

/**
 * useReopenChecklistItem — desfaz/reabre um item
 * (`POST /v1/checklist-items/:id/reopen`).
 *
 * Antes desta rota não havia porta de volta: `fulfill` e `mark-not-applicable`
 * recusavam item não-pendente, então um clique errado era irreversível pela
 * aplicação. A política de quando exige motivo é do engine — aqui só passamos
 * o que o operador deu, e o 400 volta com a mensagem certa.
 */
export function useReopenChecklistItem(): ReturnType<
  typeof useMutation<void, Error, { itemId: string; reason?: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, reason }: { itemId: string; reason?: string }) =>
      api.reopenChecklistItem(itemId, reason !== undefined ? { reason } : {}),
    onSuccess: () => invalidateChecklistQueries(qc),
    onError: toastChecklistError,
  });
}
